import {
  resolveActiveBlueprintUnderlay,
  type StoreyForAssignment,
} from '@shared/helpers/design/blueprint-underlay.helpers';
import type { BlueprintUnderlay } from '@shared/models/design/blueprint-underlay.model';
import type { BlueprintInteractionMode } from '../blueprint-underlay/blueprint-underlay-controller';

const BLUEPRINT_UNDERLAY_PAGE_MENU_PREFIX = 'blueprint-underlay-page:';

/**
 * Stable menu-store id for one underlay page (kebab + right-click).
 */
export const blueprintUnderlayPageMenuId = (pageId: string): string =>
  `${BLUEPRINT_UNDERLAY_PAGE_MENU_PREFIX}${pageId}`;

/**
 * Read the page id from a {@link blueprintUnderlayPageMenuId} store key.
 */
export const parseBlueprintUnderlayPageMenuId = (
  menuId: string | number | undefined,
): string | undefined => {
  if (typeof menuId !== 'string') return undefined;
  if (!menuId.startsWith(BLUEPRINT_UNDERLAY_PAGE_MENU_PREFIX)) return undefined;
  const pageId = menuId.slice(BLUEPRINT_UNDERLAY_PAGE_MENU_PREFIX.length);
  if (!pageId) return undefined;
  return pageId;
};

/**
 * Storey levels a page can be copied to — every project storey except its own.
 */
export const getBlueprintCopyTargetLevels = (
  storeyLevelCount: number,
  pageStartLevel: number | undefined,
): number[] =>
  Array.from(
    { length: Math.max(0, storeyLevelCount) },
    (_, index) => index + 1,
  ).filter((level) => level !== pageStartLevel);

/**
 * Page id for the canvas kebab, only while the PDF is selected
 * (`transform` / `calibrate`). Locked mode returns undefined.
 */
export const resolveBlueprintContextMenuPageId = (input: {
  mode: BlueprintInteractionMode;
  underlays: readonly BlueprintUnderlay[];
  storeys: readonly StoreyForAssignment[];
  selectedStoreyId?: string;
  manualUnderlayId?: string;
}): string | undefined => {
  if (input.mode !== 'transform' && input.mode !== 'calibrate') {
    return undefined;
  }
  return resolveActiveBlueprintUnderlay({
    underlays: input.underlays,
    storeys: input.storeys,
    selectedStoreyId: input.selectedStoreyId,
    manualUnderlayId: input.manualUnderlayId,
  })?.id;
};
